import { action, makeObservable, observable, toJS } from "mobx"
import { B2BContent } from "../../../domain/entities/B2BContent"
import { GetB2BContentUseCase } from "../../../domain/useCases/b2b/getB2BContent"
import { UpdateB2BContentUseCase } from "../../../domain/useCases/b2b/updateB2BContent"
import { UpdateB2BImageUseCase } from "../../../domain/useCases/b2b/updateB2BImage"
import { ErrorHandler } from "../../error/ErrorHandler"
import { UploadFile } from "antd"
import { RcFile } from "antd/lib/upload"

interface Props {
	GetB2BContentUseCase: GetB2BContentUseCase
	UpdateB2BContentUseCase: UpdateB2BContentUseCase
	UpdateB2BImageUseCase: UpdateB2BImageUseCase
	ErrorHandler: ErrorHandler
}

type B2BContentForm = Omit<Omit<B2BContent, "wideImage">, "headerImage"> & {
	headerImage?: string | UploadFile | null
	wideImage?: string | UploadFile | null
}

export class B2BViewModel {
	private _getB2BContentUseCase
	private _updateB2BContentUseCase
	private _updateB2BImageUseCase
	private _errorHandler

	loading = false
	selectedLocale = "es-es"
	formData: { [lang: string]: B2BContentForm } = {}

	constructor({ GetB2BContentUseCase, UpdateB2BContentUseCase, UpdateB2BImageUseCase, ErrorHandler }: Props) {
		makeObservable(this, {
			formData: observable,
			selectedLocale: observable,
			loading: observable,
			setLoading: action,
			setSelectedLocale: action,
			setFormData: action,
			setSelectedLocaleFormData: action
		})
		this._getB2BContentUseCase = GetB2BContentUseCase
		this._updateB2BContentUseCase = UpdateB2BContentUseCase
		this._updateB2BImageUseCase = UpdateB2BImageUseCase
		this._errorHandler = ErrorHandler
		this.loadB2BContent()
	}

	async loadB2BContent() {
		try {
			this.setLoading(true)
			const b2bContent = await this._getB2BContentUseCase.exec()
			this.setFormData(b2bContent.reduce((content, c) => ({ ...content, [c.locale]: c }), {}))
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async updateB2B() {
		try {
			this.setLoading(true)
			await Promise.all([
				this._updateB2BContentUseCase.exec(
					Object.keys(this.formData).map(locale => ({
						...this.formData[locale],
						locale,
						headerImage: undefined,
						wideImage: undefined
					}))
				),
				this.uploadImages("headerImage"),
				this.uploadImages("wideImage")
			])
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async uploadImages(imageName: "headerImage" | "wideImage") {
		const images = Object.keys(this.formData)
			.filter(
				locale =>
					this.formData[locale][imageName] instanceof File ||
					(this.formData[locale][imageName] as UploadFile)?.originFileObj instanceof File
			)
			.map(locale => ({
				locale,
				image:
					(this.formData[locale][imageName] as UploadFile)?.originFileObj ??
					(this.formData[locale][imageName] as RcFile)
			}))
		if (images.length > 0) {
			await this._updateB2BImageUseCase.exec({ imageName, files: images })
		}
	}

	setLoading(loading: boolean) {
		this.loading = loading
	}

	setSelectedLocale(selectedLocale: string) {
		this.selectedLocale = selectedLocale
	}

	setFormData(formData: { [lang: string]: B2BContentForm }) {
		this.formData = formData
	}

	setSelectedLocaleFormData(localeFormData: B2BContentForm) {
		this.formData = {
			...this.formData,
			[this.selectedLocale]: localeFormData
		}
	}
}
